// src/pages/AdminBroadcastNotification.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

export default function AdminBroadcastNotification() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [url, setUrl] = useState("/");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [info, setInfo] = useState("");

  const handleSend = async (e) => {
    e.preventDefault();

    if (!title.trim() && !message.trim()) {
      setStatus("error");
      setInfo("Please enter at least a title or a message.");
      return;
    }

    setStatus("sending");
    setInfo("");

    try {
      const { data } = await api.post("/api/notifications/admin/broadcast", {
        title: title.trim(),
        body: message.trim(),
        url: url.trim() || "/",
      });

      console.log("broadcast response:", data);

      // Treat both { success: true } and { ok: true } as success
      const success = data && (data.success || data.ok);

      if (success) {
        // New FCM response: { sent, failed }
        // Old response (if any): { sent, removed }
        const sent =
          data.sent ??
          data.success_count ?? // just in case
          0;
        const failed =
          data.failed ??
          data.failure_count ?? // just in case
          0;
        const removed =
          data.removed ?? 0; // keep backward compatibility, but optional

        let msg = `Sent to ${sent} subscriber(s).`;
        if (failed > 0) {
          msg += ` Failed for ${failed} subscriber(s).`;
        }
        if (removed > 0) {
          msg += ` Removed ${removed} old subscription(s).`;
        }

        setStatus("success");
        setInfo(msg);
      } else {
        setStatus("error");
        setInfo(data?.message || "Server did not confirm success.");
      }
    } catch (err) {
      console.error("broadcast error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send notifications.";
      setStatus("error");
      setInfo(msg);
    }
  };

  const isSending = status === "sending";

  // Simple inline styles so it looks good even without Tailwind/Bootstrap
  const pageStyle = {
    minHeight: "100vh",
    background: "#f3f4f6",
    display: "flex",
    justifyContent: "center",
    padding: "2rem 1rem",
    boxSizing: "border-box",
  };

  const cardStyle = {
    width: "100%",
    maxWidth: "720px",
    background: "#ffffff",
    borderRadius: "16px",
    boxShadow:
      "0 10px 15px -3px rgba(15,23,42,0.12), 0 4px 6px -2px rgba(15,23,42,0.08)",
    border: "1px solid #e5e7eb",
    padding: "1.75rem 1.75rem 2rem",
    boxSizing: "border-box",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  };

  const labelStyle = {
    display: "block",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#111827",
    marginBottom: "0.25rem",
  };

  const inputStyle = {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    padding: "0.5rem 0.75rem",
    fontSize: "0.9rem",
    boxSizing: "border-box",
  };

  const textAreaStyle = {
    ...inputStyle,
    minHeight: "90px",
    resize: "vertical",
  };

  const buttonStyle = {
    border: "none",
    borderRadius: "999px",
    padding: "0.55rem 1.4rem",
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#ffffff",
    background: isSending ? "#10b981" : "#059669",
    cursor: isSending ? "default" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
  };

  const backButtonStyle = {
    border: "1px solid #d1d5db",
    borderRadius: "999px",
    padding: "0.35rem 0.9rem",
    fontSize: "0.8rem",
    background: "#ffffff",
    cursor: "pointer",
    color: "#374151",
  };

  const statusBoxStyle = {
    marginTop: "0.75rem",
    fontSize: "0.8rem",
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
  };

  const statusColor =
    status === "success"
      ? { color: "#065f46" }
      : status === "error"
      ? { color: "#b91c1c" }
      : { color: "#4b5563" };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.4rem",
                fontWeight: 700,
                color: "#111827",
                margin: 0,
              }}
            >
              Send Push Notification
            </h1>
            <p
              style={{
                margin: "0.25rem 0 0",
                fontSize: "0.8rem",
                color: "#6b7280",
              }}
            >
              Send a browser push notification to all users who enabled
              notifications via the bell icon.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            style={backButtonStyle}
          >
            ← Back to Dashboard
          </button>
        </div>

        <hr style={{ border: 0, borderTop: "1px solid #e5e7eb", margin: 0 }} />

        {/* form */}
        <form
          onSubmit={handleSend}
          style={{ marginTop: "1.25rem", display: "grid", gap: "0.9rem" }}
        >
          <div>
            <label style={labelStyle}>Title</label>
            <input
              type="text"
              style={inputStyle}
              placeholder="Yoga Event Update"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <label style={labelStyle}>Message</label>
            <textarea
              style={textAreaStyle}
              placeholder="New update from YogaPay. Tap to view details."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div>
            <label style={labelStyle}>URL to open on click</label>
            <input
              type="text"
              style={inputStyle}
              placeholder="/  or  /register  or full https:// URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <p
              style={{
                marginTop: "0.25rem",
                fontSize: "0.75rem",
                color: "#6b7280",
              }}
            >
              Example: <code>/</code> or <code>/register</code> or full URL
              like <code>https://pg.viztech.in/register</code>.
            </p>
          </div>

          <div
            style={{
              marginTop: "0.75rem",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            <button
              type="submit"
              disabled={isSending}
              style={{
                ...buttonStyle,
                opacity: isSending ? 0.8 : 1,
              }}
            >
              {isSending ? (
                <>
                  <span
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "999px",
                      border: "2px solid rgba(255,255,255,0.7)",
                      borderTopColor: "transparent",
                      animation: "spin 0.6s linear infinite",
                    }}
                  />
                  Sending…
                </>
              ) : (
                "Send Notification"
              )}
            </button>

            {status !== "idle" && (
              <div style={{ ...statusBoxStyle, ...statusColor }}>
                {status === "success" && <span>✅</span>}
                {status === "error" && <span>⚠️</span>}
                <span>
                  {info ||
                    (status === "success"
                      ? "Notification sent."
                      : "Failed to send notifications.")}
                </span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
